import React, { useMemo, useState, useCallback } from 'react';
import { ReactComponent as AnteriorSVG } from './anatomy/anterior-body.svg';
import { ReactComponent as PosteriorSVG } from './anatomy/posterior-body.svg';
import { resolveView, normalizeIds, buildRuleSets } from './utils';
import './styles.css';

export const DEFAULT_HIGHLIGHT = 'hsl(25 95% 53%)';
export const DEFAULT_INACTIVE  = 'hsl(0 0% 20%)';

/**
 * MuscleVisualizer
 *
 * Renders anterior/posterior SVG anatomy views with muscle group highlighting.
 * Progressive disclosure: clean body outline first, hover/click reveals muscle name.
 *
 * Props:
 *   muscleGroups   - array of muscle IDs to highlight
 *   view           - 'anterior' | 'posterior' | 'auto' | 'split' (default: 'auto')
 *   intensity      - optional map of muscleId -> 0|1|2|3 intensity
 *   highlightColor - CSS color for active muscles (default: SLAM orange)
 *   inactiveColor  - CSS color for inactive muscles (default: dark gray)
 *   showLabels     - overlay readable labels
 *   interactive    - allow hover/focus/click
 *   synonyms       - map e.g. 'pecs' -> 'chest'
 *   className      - additional CSS classes
 *   style          - inline styles
 *   onSelectMuscle - callback when a muscle is clicked
 */
export default function MuscleVisualizer({
  muscleGroups,
  view = 'auto',
  intensity,
  highlightColor = DEFAULT_HIGHLIGHT,
  inactiveColor = DEFAULT_INACTIVE,
  showLabels = false,
  interactive = true,
  synonyms,
  className,
  style,
  onSelectMuscle
}) {
  const [hoveredMuscle, setHoveredMuscle] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const ids = useMemo(
    () => normalizeIds(muscleGroups, synonyms),
    [muscleGroups, synonyms]
  );

  const { showAnterior, showPosterior } = useMemo(
    () => resolveView(ids, view),
    [ids, view]
  );

  const css = useMemo(
    () => buildRuleSets(ids, intensity),
    [ids, intensity]
  );

  const rootStyle = {
    '--mv-highlight': highlightColor,
    '--mv-inactive': inactiveColor,
    ...style
  };

  const handleClick = useCallback((e) => {
    if (!interactive || !onSelectMuscle) return;
    const el = e.target;
    const candidate = el.closest('[id]');
    const id = candidate?.id;
    if (id && ids.includes(id)) onSelectMuscle(id);
  }, [interactive, onSelectMuscle, ids]);

  const handleMouseMove = useCallback((e) => {
    if (!interactive) return;
    const el = e.target;
    const candidate = el.closest('[id]');
    const id = candidate?.id;
    if (id && ids.includes(id)) {
      const rect = e.currentTarget.getBoundingClientRect();
      setHoveredMuscle(id);
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top - 32,
      });
    } else {
      setHoveredMuscle(null);
    }
  }, [interactive, ids]);

  const handleMouseLeave = useCallback(() => {
    setHoveredMuscle(null);
  }, []);

  const formatMuscleName = (id) => {
    if (!id) return '';
    return id
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div className={`mv-root ${className ?? ''}`} style={rootStyle}>
      <style>{css}</style>

      <div className={`mv-canvas ${showPosterior && showAnterior ? 'mv-split' : ''}`}>
        {showAnterior && (
          <div
            className="mv-pane"
            aria-label="Anterior view"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <AnteriorSVG className="mv-svg" onClick={handleClick} />
            {showLabels && <Labels forView="anterior" activeIds={ids} />}
            {hoveredMuscle && (
              <div
                className="absolute z-10 rounded-lg border border-orange-400/50 bg-gray-900/95 px-2.5 py-1.5 text-[11px] font-medium text-orange-200 pointer-events-none shadow-lg backdrop-blur-sm"
                style={{ left: tooltipPos.x, top: tooltipPos.y, transform: 'translateX(-50%)' }}
              >
                {formatMuscleName(hoveredMuscle)}
              </div>
            )}
          </div>
        )}
        {showPosterior && (
          <div
            className="mv-pane"
            aria-label="Posterior view"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <PosteriorSVG className="mv-svg" onClick={handleClick} />
            {showLabels && <Labels forView="posterior" activeIds={ids} />}
            {hoveredMuscle && (
              <div
                className="absolute z-10 rounded-lg border border-orange-400/50 bg-gray-900/95 px-2.5 py-1.5 text-[11px] font-medium text-orange-200 pointer-events-none shadow-lg backdrop-blur-sm"
                style={{ left: tooltipPos.x, top: tooltipPos.y, transform: 'translateX(-50%)' }}
              >
                {formatMuscleName(hoveredMuscle)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Basic label overlay (optional)
function Labels({ forView, activeIds }) {
  return <div className="mv-label-layer" data-view={forView} />;
}
